import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { productsApi, stockApi } from '../api/client';
import Input from '../components/Input';
import Button from '../components/Button';
import useStore from '../store/useStore';
import { parseQrCode } from '../utils/qrParser';

const ProductForm = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { scannedQr, clearScannedQr } = useStore();
  const isEdit = !!id;

  // Варианты диоптрий
  const dioptryOptions = [];
  
  // Начальные значения: 0, 1.00, 1.25, 1.50, 1.75
  dioptryOptions.push(0, 1.00, 1.25, 1.50, 1.75);
  
  // Шаг 0.25 до 5.00 (от 2.00 до 5.00 включительно)
  for (let i = 2.00; i <10.00; i += 0.25) {
    dioptryOptions.push(parseFloat(i.toFixed(2)));
  }
  
  // Шаг 0.50 до 7.00 (от 5.50 до 7.00 включительно)
  // for (let i = 5.50; i <= 7.00; i += 0.50) {
  //   dioptryOptions.push(parseFloat(i.toFixed(2)));
  // }

  // Ключ для сохранения черновика формы в localStorage
  const STORAGE_KEY = 'productForm_draft';

  // Восстанавливаем данные из localStorage при создании нового товара
  const getInitialFormData = () => {
    if (isEdit) {
      return {
        name: '',
        qr_code: '',
        price: '',
        description: '',
        product_code: '',
        item_code: '',
        diameter: '',
        shelf_life_days: '',
        initial_stock: '',
        initial_stock_dioptry: '',
      };
    }
    
    // Для создания товара пытаемся восстановить из localStorage
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          name: parsed.name || '',
          qr_code: parsed.qr_code || '',
          price: parsed.price || '',
          description: parsed.description || '',
          product_code: parsed.product_code || '',
          item_code: parsed.item_code || '',
          diameter: parsed.diameter || '',
          shelf_life_days: parsed.shelf_life_days || '',
          initial_stock: parsed.initial_stock || '',
          initial_stock_dioptry: parsed.initial_stock_dioptry || '',
        };
      }
    } catch (error) {
      console.error('Ошибка при восстановлении формы из localStorage:', error);
    }
    
    return {
      name: '',
      qr_code: '',
      price: '',
      description: '',
      product_code: '',
      item_code: '',
      diameter: '',
      shelf_life_days: '',
      initial_stock: '',
      initial_stock_dioptry: '',
    };
  };

  const [formData, setFormData] = useState(getInitialFormData);
  
  const [isInitialized, setIsInitialized] = useState(false);

  // Сохраняем форму в localStorage при каждом изменении (только для создания товара)
  useEffect(() => {
    if (!isEdit && isInitialized) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
      } catch (error) {
        console.error('Ошибка при сохранении формы в localStorage:', error);
      }
    }
  }, [formData, isEdit, isInitialized]);

  const { data: product } = useQuery({
    queryKey: ['product', id],
    queryFn: async () => {
      const response = await productsApi.getById(id);
      return response.data;
    },
    enabled: isEdit,
  });

  // Очищаем localStorage при переходе на страницу редактирования
  useEffect(() => {
    if (isEdit) {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        console.error('Ошибка при очистке localStorage:', error);
      }
    }
  }, [isEdit]);

  // Заполнение формы данными товара при редактировании
  useEffect(() => {
    if (product && isEdit) {
      console.log('📝 Loading product data for editing:', product);
      setFormData({
        name: product.name || '',
        qr_code: product.qr_code || '',
        price: product.price ? product.price.toString() : '',
        description: product.description || '',
        product_code: product.product_code || '',
        item_code: product.item_code || '',
        // Диаметр: преобразуем в строку
        diameter: product.diameter !== null && product.diameter !== undefined ? product.diameter.toString() : '',
        // Срок использования: преобразуем в строку
        shelf_life_days: product.shelf_life_days !== null && product.shelf_life_days !== undefined ? product.shelf_life_days.toString() : '',
        initial_stock: '',
        initial_stock_dioptry: '',
      });
    }
  }, [product, isEdit]);

  // Автозаполнение формы из QR-кода или item_code при создании нового товара
  useEffect(() => {
    if (!isEdit && !product && !isInitialized) {
      const qrValue = searchParams.get('qr') || scannedQr || '';
      const itemCodeParam = searchParams.get('item_code') || '';
      
      // Проверяем, есть ли сохраненные данные
      const hasSavedData = (() => {
        try {
          const saved = localStorage.getItem(STORAGE_KEY);
          if (saved) {
            const parsed = JSON.parse(saved);
            // Проверяем, есть ли хотя бы одно заполненное поле (кроме пустых строк)
            return Object.values(parsed).some(value => value && value.toString().trim() !== '');
          }
        } catch (error) {
          return false;
        }
        return false;
      })();
      
      // Если есть сохраненные данные и нет явно переданного QR-кода или item_code, не перезаписываем
      if (hasSavedData && !qrValue && !itemCodeParam) {
        setIsInitialized(true);
        return;
      }
      
      // Если передан item_code, заполняем его (даже если есть сохраненные данные)
      if (itemCodeParam) {
        setFormData(prev => ({
          ...prev,
          item_code: itemCodeParam.toUpperCase(),
        }));
      }
      
      if (qrValue) {
        console.log('📝 Processing QR code for autofill:', qrValue);
        console.log('📝 scannedQr from store:', scannedQr);
        console.log('📝 QR from searchParams:', searchParams.get('qr'));
        
        const qrData = parseQrCode(qrValue);
        console.log('📝 Parsed QR data:', qrData);
        
        // Обновляем форму с данными из QR-кода
        setFormData(prev => {
          console.log('🔄 Updating form with QR data');
          console.log('📝 Previous form data:', prev);
          console.log('📝 Parsed QR data:', qrData);
          
          // Создаем объект обновления, заполняя только те поля, которые есть в qrData
          const updates = {
            qr_code: qrValue,
          };
          
          // Заполняем поля из распарсенных данных (если они не null/undefined)
          if (qrData?.product_code != null) {
            updates.product_code = qrData.product_code;
          }
          const updated = {
            ...prev,
            ...updates,
          };
          
          console.log('📝 Updated form data:', updated);
          console.log('📝 Fields filled:', {
            product_code: updates.product_code !== undefined,
          });
          
          return updated;
        });
        
        setIsInitialized(true);
        
        const filledFields = [];
        if (qrData?.product_code) filledFields.push('product_code');
        
        if (filledFields.length > 0) {
          console.log('✅ Form fields auto-filled from QR code:', filledFields);
        } else {
          console.warn('⚠️ QR code format not fully recognized, only QR code field filled');
          console.warn('⚠️ Parsed data:', qrData);
        }
      } else {
        // Если QR-кода нет, просто отмечаем как инициализированную
        setIsInitialized(true);
      }
    }
  }, [scannedQr, searchParams, isEdit, product, isInitialized]);

  // Автозаполнение при изменении QR-кода
  const handleQrCodeChange = (e) => {
    const qrValue = e.target.value;
    
    // Парсим QR-код и заполняем поля
    const qrData = parseQrCode(qrValue);
    console.log('🔄 handleQrCodeChange - Parsed QR data:', qrData);
    
    setFormData(prev => {
      // Создаем объект обновления, заполняя только те поля, которые есть в qrData
      const updates = {
        qr_code: qrValue,
      };
      
      // Заполняем поля из распарсенных данных (если они не null/undefined)
      if (qrData?.product_code != null) {
        updates.product_code = qrData.product_code;
      }
      
      const updated = {
        ...prev,
        ...updates,
      };
      
      console.log('📝 handleQrCodeChange - Updated form data:', updated);
      
      return updated;
    });
  };

  const mutation = useMutation({
    mutationFn: async (data) => {
      if (isEdit) {
        return productsApi.update(id, data);
      }
      const response = await productsApi.create(data);
      return response;
    },
    onSuccess: async (response, variables) => {
      // Если создаем новый товар и указано начальное количество
      if (!isEdit && formData.initial_stock && formData.initial_stock.trim() !== '') {
        // API возвращает объект товара напрямую
        const product = response.data;
        const productId = product?.id;
        const qty = parseFloat(formData.initial_stock);
        const dioptry = formData.initial_stock_dioptry && formData.initial_stock_dioptry.trim() !== ''
          ? parseFloat(formData.initial_stock_dioptry)
          : null;
        
        if (productId && qty > 0) {
          try {
            await stockApi.stockIn(productId, qty, dioptry);
            queryClient.invalidateQueries(['stock']);
          } catch (error) {
            console.error('Ошибка при добавлении товара на склад:', error);
            // Не прерываем процесс, просто логируем ошибку
          }
        }
      }
      
      queryClient.invalidateQueries(['products']);
      clearScannedQr();
      
      // Очищаем сохраненный черновик после успешного создания
      if (!isEdit) {
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch (error) {
          console.error('Ошибка при очистке localStorage:', error);
        }
      }
      
      navigate('/products');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Убираем initial_stock и initial_stock_dioptry из данных товара, так как это отдельная операция
    const { initial_stock, initial_stock_dioptry, ...productData } = formData;
    
    mutation.mutate({
      ...productData,
      price: parseFloat(productData.price),
      // Преобразуем пустые строки в null для опциональных полей
      qr_code: productData.qr_code || null,
      product_code: productData.product_code || null,
      item_code: productData.item_code || null,
      diameter: productData.diameter ? parseFloat(productData.diameter) : null,
      shelf_life_days: productData.shelf_life_days ? parseInt(productData.shelf_life_days) : null,
      description: productData.description || null,
    });
  };

  return (
    <div className="p-4">
      <div className="mb-4">
        <button
          onClick={() => navigate('/products')}
          className="text-blue-600 mb-4"
        >
          ← Назад
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Редактировать товар' : 'Создать товар'}
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        <Input
          label="Название товара"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Введите название товара"
          required
        />

        <Input
          label="Артикул"
          value={formData.item_code}
          onChange={(e) => setFormData({ ...formData, item_code: e.target.value.toUpperCase() })}
          placeholder="Введите артикул товара"
        />

        <Input
          label="DIA (мм)"
          type="number"
          step="0.1"
          value={formData.diameter}
          onChange={(e) => setFormData({ ...formData, diameter: e.target.value })}
          placeholder="0.0"
        />

        <Input
          label="Срок использования (дней)"
          type="number"
          value={formData.shelf_life_days}
          onChange={(e) => setFormData({ ...formData, shelf_life_days: e.target.value })}
          placeholder="Количество дней"
          min="1"
        />

        <Input
          label="Цена"
          type="number"
          step="0.01"
          value={formData.price}
          onChange={(e) => setFormData({ ...formData, price: e.target.value })}
          placeholder="0.00"
          required
        />

        
        {!isEdit && (
          <>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Диоптрии для начального прихода
              </label>
              <select
                value={formData.initial_stock_dioptry}
                onChange={(e) => setFormData({ ...formData, initial_stock_dioptry: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-base"
              >
                <option value="">Не указано</option>
                {dioptryOptions.map((value) => (
                  <option key={value} value={value}>
                    {value === 0 ? '0' : "-" + value.toFixed(2)}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label="Начальное количество на складе"
              type="number"
              step="0.1"
              min="0"
              value={formData.initial_stock}
              onChange={(e) => setFormData({ ...formData, initial_stock: e.target.value })}
              placeholder="Укажите количество для прихода"
            />
           
          </>
        )}

        <Input
          label="Описание"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Необязательное описание"
        />

        {/* <Input
          label="QR-код"
          value={formData.qr_code}
          onChange={handleQrCodeChange}
          placeholder="Введите или отсканируйте QR-код (необязательно)"
        />

        <Input
          label="Код товара"
          value={formData.product_code}
          onChange={(e) => setFormData({ ...formData, product_code: e.target.value })}
          placeholder="Введите код товара (необязательно)"
        /> */}


        {mutation.error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
            {mutation.error.response?.data?.error || 'Произошла ошибка'}
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Сохранение...' : isEdit ? 'Обновить товар' : 'Создать товар'}
        </Button>
      </form>
    </div>
  );
};

export default ProductForm;

